/**
 * API Configuration for AWS Bedrock & Lambda Agents
 * Also supports SR Backend API for InfluxDB and DynamoDB queries
 */

// Bedrock Agents API (existing)
const BEDROCK_API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 
  'https://xhy16kjhe9.execute-api.me-central-1.amazonaws.com/prod';

// SR Backend API (for InfluxDB, DynamoDB, Excel)
const SR_BACKEND_API_BASE_URL = process.env.REACT_APP_SR_BACKEND_API_URL || 
  'https://pzmfylij51.execute-api.me-central-1.amazonaws.com/prod';

// Default API base URL (can be overridden)
const API_BASE_URL = BEDROCK_API_BASE_URL;

// Export SR Backend API URL for services that need it
export const SR_BACKEND_API_URL = SR_BACKEND_API_BASE_URL;

// Store auth token (can be set manually or auto-retrieved from Amplify)
let authToken = null;

/**
 * Set the auth token manually (optional - API client can also get it from Amplify)
 */
export const setAuthToken = (token) => {
  authToken = token;
};

/**
 * Get the current auth token
 */
export const getAuthToken = () => {
  return authToken;
};

class ApiClient {
  constructor(baseURL) {
    this.baseURL = baseURL;
  }

  async request(endpoint, options = {}) {
    const url = `${this.baseURL}${endpoint}`;
    
    // Get auth token if available
    // Priority: 1. Manually set token, 2. Amplify session
    let tokenToUse = authToken;
    
    if (!tokenToUse) {
      try {
        // Try to get token from Amplify session
        const { fetchAuthSession } = await import('aws-amplify/auth');
        const session = await fetchAuthSession();
        tokenToUse = session.tokens?.idToken?.toString();
      } catch (error) {
        // Token not available, continue without it
        console.log('No auth token available');
      }
    }
    
    const headers = {
      'Content-Type': 'application/json',
      ...options.headers,
    };
    
    // Add Authorization header if token is available
    if (tokenToUse) {
      headers['Authorization'] = `Bearer ${tokenToUse}`;
    }

    try {
      console.log('Making API request to:', url);
      console.log('Request options:', { method: options.method, headers, body: options.body });
      
      const fetchOptions = {
        ...options,
        headers,
        mode: 'cors', // Explicitly enable CORS
        credentials: 'omit', // Don't send cookies
        cache: 'no-cache', // Don't cache
      };
      
      console.log('Fetch options:', fetchOptions);
      
      const response = await fetch(url, fetchOptions);

      console.log('Response status:', response.status);
      console.log('Response headers:', [...response.headers.entries()]);

      if (!response.ok) {
        // Handle 403 Forbidden - likely CORS or authorization issue
        if (response.status === 403) {
          const errorText = await response.text().catch(() => '');
          let errorData = {};
          try {
            errorData = errorText ? JSON.parse(errorText) : {};
          } catch (e) {
            // Not JSON, use text as message
            errorData = { message: errorText || 'Forbidden: Access denied' };
          }
          
          const error = new Error(
            errorData.message || 
            errorData.error || 
            'Forbidden: The API Gateway may not be configured to accept requests from this origin, or the authorization token is invalid.'
          );
          error.status = 403;
          error.statusText = 'Forbidden';
          console.error('API 403 Error:', error);
          throw error;
        }
        
        // Handle other errors
        const errorText = await response.text().catch(() => '');
        let errorData = {};
        try {
          errorData = errorText ? JSON.parse(errorText) : {};
        } catch (e) {
          errorData = { message: errorText || `API Error: ${response.status} ${response.statusText}` };
        }
        
        const error = new Error(
          errorData.message || 
          errorData.error || 
          `API Error: ${response.status} ${response.statusText}`
        );
        error.status = response.status;
        error.statusText = response.statusText;
        console.error('API Error:', error);
        throw error;
      }

      const data = await response.json();
      console.log('API Response:', data);
      return data;
    } catch (error) {
      // Handle network errors (CORS, connection issues)
      if (error.message === 'Load failed' || error.message === 'Failed to fetch' || error.name === 'TypeError') {
        const networkError = new Error(
          'Network error: Unable to connect to the API. This may be due to CORS configuration, network connectivity, or the API endpoint being unavailable.'
        );
        networkError.originalError = error;
        networkError.isNetworkError = true;
        console.error('API Network Error:', networkError);
        console.error('Original error:', error);
        throw networkError;
      }
      
      console.error('API Request failed:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        url: url,
        status: error.status,
        statusText: error.statusText
      });
      throw error;
    }
  }

  async healthCheck() {
    return this.request('/query', {
      method: 'POST',
      body: JSON.stringify({
        action: 'health',
      }),
    });
  }

  async query(question, userContext = null) {
    // Build request body with user context
    const requestBody = {
      action: 'query',
      query: question,
    };
    
    // Add user context if provided
    if (userContext) {
      requestBody.userContext = userContext;
    }
    
    return this.request('/query', {
      method: 'POST',
      body: JSON.stringify(requestBody),
    });
  }
}

const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;

